import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Sparkles, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "What should I eat for dinner?",
  "How many calories do I have left?",
  "Give me a meal under 500 calories.",
  "What is a high-protein breakfast?",
];

export default function Coach() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Hi ${(profile?.name || "there").split(" ")[0]}! I'm your Calorify coach. Ask me anything about your nutrition today.` },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const r = await api.coach(msg);
      setMessages((m) => [...m, { role: "assistant", text: r.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't respond right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col max-w-md mx-auto">
      <div className="flex items-center gap-3 px-5 pt-6 pb-3 border-b border-border bg-card/80 backdrop-blur">
        <button onClick={() => navigate(-1)} data-testid="coach-back" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
            <Sparkles className="w-4.5 h-4.5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display font-medium leading-tight">AI Coach</div>
            <div className="text-xs text-primary">Online</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-3" data-testid="coach-messages">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
              {m.text}
            </div>
          </motion.div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-2xl bg-card border border-border">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="px-5 pb-2 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} data-testid="coach-suggestion" className="text-xs px-3 py-2 rounded-full bg-secondary text-secondary-foreground hover:bg-primary/10 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="px-5 py-4 border-t border-border flex items-center gap-2">
        <Input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask your coach…"
          className="h-12 rounded-full bg-card"
        />
        <button onClick={() => send()} disabled={busy} data-testid="coach-send" className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 disabled:opacity-50 hover:bg-primary/90 transition-colors">
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
